import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ComposedChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LabelList,
  ResponsiveContainer,
  type TooltipContentProps,
  type TooltipPayloadEntry,
} from 'recharts';
import { Card, CardHeader, CardContent } from '@cortex/web-design-system/components/ui/Card';
import { Select } from '@cortex/web-design-system/components/ui/Select';
import {
  BAR_MAX_SIZE,
  BAR_RADIUS_TOP,
  stackedBarRadius,
  createCompactLegendContent,
  TEAM_BREAKDOWN_ORDER,
} from './chart-styles.js';
import { formatPt } from '../../../lib/format.js';
import type {
  AceMonthlyTrendPoint,
  KpiMonthlyTrendBreakdownKey,
  KpiTeam,
} from '../../../types/api.js';

interface AceMonthlyTrendChartProps {
  /** 月次トレンドデータ */
  data: AceMonthlyTrendPoint[];
  /** ローディング状態 */
  isLoading: boolean;
  /** 選択中の表示月数 */
  selectedMonths: string;
  /** 表示月数変更コールバック */
  onMonthsChange: (months: string) => void;
  /** 親のチームフィルタ。内訳ラベル選択に使用 ('all'=未指定相当) */
  team?: KpiTeam;
}

/** @graph-connects none */
const MONTHS_OPTIONS = [
  { value: '6', label: '6ヶ月' },
  { value: '12', label: '12ヶ月' },
  { value: '18', label: '18ヶ月' },
  { value: '24', label: '24ヶ月' },
  { value: '36', label: '36ヶ月' },
];

/**
 * 実績pt 内訳キー → 色 のマッピング。target も同じ hex を使い、
 * 描画側で fillOpacity を変えて「target = 代表色の 60%」を表現する。
 *  - PDG=#FFB33F / ACE=#C00707 (scope='all' トップグループ)
 *  - ACE 内訳: airCloset=#CB3030, Platform=#40CEEE, Mall=#00EAB7, QA=#FFE97B
 *  - PDG 内訳: PDG=#FFB33F, SRE=#4B6070, TE=#EDC818
 *  - 'ace' キーは scope='ace' の時は ACE 未割当(灰)、それ以外は ACE グループ(#C00707)
 *    を意味する。前者は getActualColor/getTargetColor で上書き
 * @graph-connects none
 */
const ACTUAL_COLORS: Record<KpiMonthlyTrendBreakdownKey, string> = {
  pdg: '#FFB33F',
  ace: '#C00707',
  tet: '#EDC818',
  sre: '#4B6070',
  aircloset: '#CB3030',
  platform: '#40CEEE',
  mall: '#00EAB7',
  qa: '#FFE97B',
};

/** target も actual と同 hex。描画時に fillOpacity=TARGET_FILL_OPACITY で 60% に落とす @graph-connects none */
const TARGET_COLORS: Record<KpiMonthlyTrendBreakdownKey, string> = ACTUAL_COLORS;

/** ACE 未割当 (team='ace' 内訳の 'ace' キー) target 用のニュートラル色 @graph-connects none */
const ACE_REST_TARGET_COLOR = '#9ca3af';
/** ACE 未割当 (team='ace' 内訳の 'ace' キー) actual 用のニュートラル色 @graph-connects none */
const ACE_REST_ACTUAL_COLOR = '#9ca3af';

/** actual bar の fillOpacity。代表色そのまま @graph-connects none */
const ACTUAL_FILL_OPACITY = 1;
/** target bar の fillOpacity。代表色の 60% で「半透明 target」を表現 @graph-connects none */
const TARGET_FILL_OPACITY = 0.6;

/** 親 team を考慮して target 色を解決 (team='ace' の 'ace' は ACE 未割当=灰) @graph-connects none */
function getTargetColor(key: KpiMonthlyTrendBreakdownKey, parentTeam: KpiTeam | undefined): string {
  if (parentTeam === 'ace' && key === 'ace') return ACE_REST_TARGET_COLOR;
  return TARGET_COLORS[key];
}

/** 親 team を考慮して actual 色を解決 (team='ace' の 'ace' は ACE 未割当=灰) @graph-connects none */
function getActualColor(key: KpiMonthlyTrendBreakdownKey, parentTeam: KpiTeam | undefined): string {
  if (parentTeam === 'ace' && key === 'ace') return ACE_REST_ACTUAL_COLOR;
  return ACTUAL_COLORS[key];
}

/** Person-month 折れ線の水色（全チャート統一） @graph-connects none */
const PERSON_MONTH_COLOR = '#38bdf8';

/**
 * 親 team フィルタを踏まえて内訳キーを表示用 i18n key に解決
 *  - team='pdg' の 'pdg' キーは「PDG (TE/SRE除く)」と読みたいので pdgRest を返す
 *  - team='ace' の 'ace' キーは「ACE (未割当)」と読みたいので aceRest を返す
 *  - 内訳ラベルは週次と共通の kpi.weeklyActualBreakdown を流用
 * @graph-connects none
 */
function getBreakdownI18nKey(
  key: KpiMonthlyTrendBreakdownKey,
  parentTeam: KpiTeam | undefined,
): string {
  if (parentTeam === 'pdg' && key === 'pdg') return 'kpi.weeklyActualBreakdown.pdgRest';
  if (parentTeam === 'ace' && key === 'ace') return 'kpi.weeklyActualBreakdown.aceRest';
  return `kpi.weeklyActualBreakdown.${key}`;
}

/** 月初日 YYYY-MM-DD を "YY/M" 表示 @graph-connects none */
function formatMonthLabel(month: string): string {
  const parts = month.split('-');
  if (parts.length < 2) return month;
  const year = String(Number(parts[0]) % 100).padStart(2, '0');
  return `${year}/${String(Number(parts[1]))}`;
}

/**
 * 内訳キーの安定表示順。Weekly chart と共用する `TEAM_BREAKDOWN_ORDER` を
 * `KpiMonthlyTrendBreakdownKey` 経由で参照する (`KpiMonthlyTrendBreakdownKey`
 *  は `KpiWeeklyTrendBreakdownKey` の同一エイリアスなのでキャスト不要)。
 * @graph-connects none
 */
const BREAKDOWN_ORDER: readonly KpiMonthlyTrendBreakdownKey[] = TEAM_BREAKDOWN_ORDER;

/**
 * recharts は stackId 単位で tooltip / legend をグルーピングし宣言順を無視するため、
 * チーム×指標の表示順を明示的に決める sort key を返す。
 * 並びは「team ごとに target → actual を交互配置」+ 末尾に personMonth。
 *  - target/<key> → BREAKDOWN_ORDER 位置 * 2
 *  - actual/<key> → BREAKDOWN_ORDER 位置 * 2 + 1
 *  - personMonth → BREAKDOWN_ORDER.length * 2
 *  - 単色フォールバック (target/actual) は team 別の前に置く
 * @graph-connects none
 */
function getDisplaySortKey(dataKey: string): number {
  if (dataKey === 'personMonth') return BREAKDOWN_ORDER.length * 2;
  if (dataKey === 'target') return -2;
  if (dataKey === 'actual') return -1;
  if (dataKey.startsWith('targetByTeam_')) {
    const key = dataKey.slice('targetByTeam_'.length) as KpiMonthlyTrendBreakdownKey;
    const idx = BREAKDOWN_ORDER.indexOf(key);
    return idx < 0 ? 999 : idx * 2;
  }
  if (dataKey.startsWith('actualByTeam_')) {
    const key = dataKey.slice('actualByTeam_'.length) as KpiMonthlyTrendBreakdownKey;
    const idx = BREAKDOWN_ORDER.indexOf(key);
    return idx < 0 ? 999 : idx * 2 + 1;
  }
  return 1000;
}

type AceTooltipEntry = TooltipPayloadEntry;

interface AceTooltipContentProps {
  active?: boolean;
  payload?: readonly AceTooltipEntry[];
  label?: string | number;
  parentTeam: KpiTeam | undefined;
  t: (key: string) => string;
}

/**
 * 自前実装の tooltip 内容。recharts 3.x の Tooltip は ComposedChart + 多 stack 構成で
 * 一部 payload を欠落させる挙動（5 スタック以上の Bar + Line の組み合わせで再現）が
 * あるため、recharts のフィルタ / 並び替えを迂回して 全 payload エントリを表示する。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 月次トレンド tooltip の自前実装。全 payload を希望順 (team ごとに target → actual + 末尾に personMonth) で漏れなく表示する
 * @graph-connects none
 */
function AceTooltipContent({ active, payload, label, parentTeam, t }: AceTooltipContentProps) {
  if (active !== true || payload == null || payload.length === 0) return null;

  const sorted = [...payload].sort((a, b) => {
    const dkA = String(a.dataKey ?? a.name ?? '');
    const dkB = String(b.dataKey ?? b.name ?? '');
    return getDisplaySortKey(dkA) - getDisplaySortKey(dkB);
  });

  const labelText = typeof label === 'string' ? formatMonthLabel(label) : String(label ?? '');

  return (
    <div
      style={{
        backgroundColor: 'var(--color-bg-elevated)',
        border: '1px solid var(--color-border)',
        borderRadius: '8px',
        padding: '8px 10px',
        opacity: 1,
        fontSize: 12,
        lineHeight: '18px',
        boxShadow: 'var(--shadow-lg)',
      }}
    >
      <div style={{ color: 'var(--color-text-primary)', marginBottom: 4, fontWeight: 500 }}>
        {labelText}
      </div>
      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {sorted.map((entry, i) => {
          const dk = String(entry.dataKey ?? entry.name ?? '');
          const rawValue = entry.value;
          const numeric =
            typeof rawValue === 'number'
              ? rawValue
              : Array.isArray(rawValue)
                ? Number(rawValue[rawValue.length - 1] ?? 0)
                : Number(rawValue ?? 0);
          const color = entry.color ?? entry.fill ?? 'var(--color-text-primary)';

          let displayLabel = dk;
          let displayValue = `${formatPt(numeric)}pt`;
          if (dk === 'target') {
            displayLabel = t('kpi.monthlyTarget');
          } else if (dk === 'actual') {
            displayLabel = t('kpi.monthlyActual');
          } else if (dk === 'personMonth') {
            displayLabel = t('kpi.personMonth');
            displayValue = String(numeric);
          } else if (dk.startsWith('targetByTeam_')) {
            const teamKey = dk.slice('targetByTeam_'.length) as KpiMonthlyTrendBreakdownKey;
            displayLabel = `${t('kpi.monthlyTarget')} / ${t(getBreakdownI18nKey(teamKey, parentTeam))}`;
          } else if (dk.startsWith('actualByTeam_')) {
            const teamKey = dk.slice('actualByTeam_'.length) as KpiMonthlyTrendBreakdownKey;
            displayLabel = `${t('kpi.monthlyActual')} / ${t(getBreakdownI18nKey(teamKey, parentTeam))}`;
          }

          return (
            <li key={`${dk}-${String(i)}`} style={{ color, padding: '1px 0' }}>
              <span>{displayLabel}</span>
              <span style={{ marginLeft: 4 }}>: {displayValue}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/**
 * ACE 月次トレンドチャートカード
 *  - Bar: Target / Actual を左軸（チーム別内訳がある月はスタック表示、無い月は単色）、Line: Person-month を右軸
 *  - スプレッドシート「ace_まとめ」と同等のレイアウト
 *  - team=all/pdg/ace の場合は byTeam 内訳を target=青系 / actual=赤系で色分けし、
 *    濃淡で「親グループ＝濃い、子＝薄い」を表現
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business ACE組織の月次目標pt/実績pt/人月を複合グラフで表示。Target/Actual は kpi_develop_point_detail を月集計、Person-month は capacity_by_team_month から導出。team=all/pdg/ace 時は byTeam 内訳で色分け（青系=目標、赤系=実績、濃淡=グループ階層）
 * @graph-connects Card [calls] カードコンポーネント描画
 * @graph-connects CardHeader [calls] カードヘッダー描画
 * @graph-connects CardContent [calls] カードコンテンツ描画
 * @graph-connects Select [calls] 表示月数セレクト描画
 */
export function AceMonthlyTrendChart({
  data,
  isLoading,
  selectedMonths,
  onMonthsChange,
  team,
}: AceMonthlyTrendChartProps) {
  const { t } = useTranslation();

  const breakdownKeys = useMemo<KpiMonthlyTrendBreakdownKey[]>(() => {
    const seen = new Set<KpiMonthlyTrendBreakdownKey>();
    for (const row of data) {
      if (row.byTeam) {
        for (const entry of row.byTeam) {
          seen.add(entry.team);
        }
      }
    }
    return BREAKDOWN_ORDER.filter((k) => seen.has(k));
  }, [data]);

  const hasBreakdown = breakdownKeys.length > 0;

  const chartData = useMemo(
    () =>
      data.map((row) => {
        const byTeamTarget: Record<string, number> = {};
        const byTeamActual: Record<string, number> = {};
        if (row.byTeam) {
          for (const entry of row.byTeam) {
            byTeamTarget[`targetByTeam_${entry.team}`] = entry.target ?? 0;
            byTeamActual[`actualByTeam_${entry.team}`] = entry.actual ?? 0;
          }
        }
        // 不足キーを 0 で埋めて recharts のスタック整合性を維持
        for (const key of breakdownKeys) {
          byTeamTarget[`targetByTeam_${key}`] ??= 0;
          byTeamActual[`actualByTeam_${key}`] ??= 0;
        }
        return {
          month: row.month,
          target: row.target,
          actual: row.actual,
          personMonth: row.personMonth,
          ...byTeamTarget,
          ...byTeamActual,
        };
      }),
    [data, breakdownKeys],
  );

  const hasData =
    data.length > 0 &&
    data.some((d) => (d.target !== null && d.target > 0) || d.actual > 0 || d.personMonth > 0);

  return (
    <Card className="animate-content-reveal">
      <CardHeader title={t('kpi.monthlyTrend')} description={t('kpi.monthlyTrendDescription')} />
      <CardContent>
        <div className="flex justify-end mb-3">
          <Select
            options={MONTHS_OPTIONS}
            value={selectedMonths}
            onChange={(e) => {
              onMonthsChange(e.target.value);
            }}
            size="sm"
          />
        </div>
        {isLoading ? (
          <div className="h-56 rounded-lg bg-[var(--color-bg-elevated)] animate-pulse" />
        ) : hasData ? (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData} margin={{ top: 20, right: 30, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis
                  dataKey="month"
                  stroke="var(--color-text-muted)"
                  fontSize={11}
                  tickFormatter={formatMonthLabel}
                />
                <YAxis
                  yAxisId="left"
                  stroke="var(--color-text-muted)"
                  fontSize={11}
                  tickFormatter={(value: number) => formatPt(value)}
                />
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  stroke="var(--color-text-muted)"
                  fontSize={11}
                  tickFormatter={(value: number) => String(value)}
                />
                <Tooltip
                  // wrapperStyle で opacity=1 を明示しないと、recharts の default が
                  // ドラッグ操作時に half-opacity を当てて背景が透けて見える。
                  // content は自前実装で、recharts default の payload フィルタ /
                  // 表示数制限を迂回して全 payload エントリを表示する
                  wrapperStyle={{ opacity: 1, zIndex: 50 }}
                  filterNull={false}
                  content={(props: TooltipContentProps) => (
                    <AceTooltipContent
                      active={props.active}
                      payload={props.payload}
                      label={props.label}
                      parentTeam={team}
                      t={t}
                    />
                  )}
                />
                <Legend
                  content={createCompactLegendContent(
                    (value) => {
                      if (value === 'actual') return t('kpi.monthlyActual');
                      if (value === 'personMonth') return t('kpi.personMonth');
                      if (value.startsWith('actualByTeam_')) {
                        // user 要望: actual のみ表示し、ラベルはチーム名だけ ("Actual /" 接頭は出さない)
                        const teamKey = value.slice(
                          'actualByTeam_'.length,
                        ) as KpiMonthlyTrendBreakdownKey;
                        return t(getBreakdownI18nKey(teamKey, team));
                      }
                      return value;
                    },
                    {
                      // target 系 (target / targetByTeam_*) と
                      // ACE 未割当 (team='ace' の 'ace') / PDG (TE/SRE除く) (team='pdg' の 'pdg')
                      // は凡例に出さない。残った actualByTeam_* + personMonth が宣言順
                      // (= actual → personMonth) で表示されるため personMonth は必ず右端
                      filter: (value) => {
                        if (value === 'target') return false;
                        if (value.startsWith('targetByTeam_')) return false;
                        if (team === 'ace' && value === 'actualByTeam_ace') return false;
                        if (team === 'pdg' && value === 'actualByTeam_pdg') return false;
                        return true;
                      },
                    },
                  )}
                />
                {hasBreakdown ? (
                  // recharts 3.x で flatMap が返すネスト配列だと末尾の Bar が
                  // tooltipPayloadConfigurations から欠落する事象があるため、
                  // 「全 target → 全 actual」の 2 段 map に展開する。
                  // 描画順は stackId で吸収され、tooltip 表示順は
                  // AceTooltipContent 内の sort (getDisplaySortKey ベース) で
                  // 「team ごとに target → actual」に揃えるので影響しない
                  <>
                    {breakdownKeys.map((key, i) => {
                      const color = getTargetColor(key, team);
                      const isTop = i === breakdownKeys.length - 1;
                      return (
                        <Bar
                          key={`target-${key}`}
                          yAxisId="left"
                          dataKey={`targetByTeam_${key}`}
                          name={`targetByTeam_${key}`}
                          stackId="target"
                          fill={color}
                          fillOpacity={TARGET_FILL_OPACITY}
                          stroke="none"
                          maxBarSize={BAR_MAX_SIZE}
                          radius={stackedBarRadius(i, breakdownKeys.length)}
                        >
                          {isTop && (
                            <LabelList
                              dataKey="target"
                              position="top"
                              fill="var(--color-text-primary)"
                              fontSize={11}
                              formatter={
                                ((value: number | null | undefined) =>
                                  value != null && value > 0
                                    ? Math.round(value).toLocaleString()
                                    : '') as never
                              }
                            />
                          )}
                        </Bar>
                      );
                    })}
                    {breakdownKeys.map((key, i) => {
                      const color = getActualColor(key, team);
                      const isTop = i === breakdownKeys.length - 1;
                      return (
                        <Bar
                          key={`actual-${key}`}
                          yAxisId="left"
                          dataKey={`actualByTeam_${key}`}
                          name={`actualByTeam_${key}`}
                          stackId="actual"
                          fill={color}
                          fillOpacity={ACTUAL_FILL_OPACITY}
                          stroke={color}
                          strokeWidth={1}
                          maxBarSize={BAR_MAX_SIZE}
                          radius={stackedBarRadius(i, breakdownKeys.length)}
                        >
                          {isTop && (
                            <LabelList
                              dataKey="actual"
                              position="top"
                              fill="var(--color-text-primary)"
                              fontSize={11}
                              formatter={
                                ((value: number | null | undefined) =>
                                  value != null && value > 0
                                    ? Math.round(value).toLocaleString()
                                    : '') as never
                              }
                            />
                          )}
                        </Bar>
                      );
                    })}
                  </>
                ) : (
                  (() => {
                    // 親 team が代表色を持つ leaf team の場合はその色を使う。
                    // API が byTeam=null を返すため単色 Bar 経路に入る
                    const fallbackColor =
                      team && team in ACTUAL_COLORS
                        ? ACTUAL_COLORS[team as KpiMonthlyTrendBreakdownKey]
                        : '#C00707';
                    return (
                      <>
                        <Bar
                          yAxisId="left"
                          dataKey="target"
                          name="target"
                          fill={fallbackColor}
                          fillOpacity={TARGET_FILL_OPACITY}
                          stroke="none"
                          maxBarSize={BAR_MAX_SIZE}
                          radius={BAR_RADIUS_TOP}
                        >
                          <LabelList
                            dataKey="target"
                            position="top"
                            fill="var(--color-text-primary)"
                            fontSize={11}
                            formatter={
                              ((value: number | null | undefined) =>
                                value != null && value > 0
                                  ? Math.round(value).toLocaleString()
                                  : '') as never
                            }
                          />
                        </Bar>
                        <Bar
                          yAxisId="left"
                          dataKey="actual"
                          name="actual"
                          fill={fallbackColor}
                          fillOpacity={ACTUAL_FILL_OPACITY}
                          stroke={fallbackColor}
                          strokeWidth={1}
                          maxBarSize={BAR_MAX_SIZE}
                          radius={BAR_RADIUS_TOP}
                        >
                          <LabelList
                            dataKey="actual"
                            position="top"
                            fill="var(--color-text-primary)"
                            fontSize={11}
                            formatter={
                              ((value: number | null | undefined) =>
                                value != null && value > 0
                                  ? Math.round(value).toLocaleString()
                                  : '') as never
                            }
                          />
                        </Bar>
                      </>
                    );
                  })()
                )}
                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="personMonth"
                  name="personMonth"
                  stroke={PERSON_MONTH_COLOR}
                  strokeWidth={2}
                  dot={{ r: 3, fill: PERSON_MONTH_COLOR }}
                  activeDot={{ r: 5, fill: PERSON_MONTH_COLOR }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="h-56 flex items-center justify-center border border-dashed border-[var(--color-border)] rounded-lg">
            <p className="text-[var(--color-text-muted)]">{t('status.noData')}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
